import { useEffect } from "react";

import type { User } from "../types";
import { PersonalSettings } from "../pages/MySettings";
import { Button } from "./ui/Button";

/**
 * An agent's own settings, over whatever they were doing.
 *
 * A dialog rather than a route, and the reason is the same one that put the
 * launcher in the sidebar: these settings belong to the person, not to the
 * screen. A route would have to live under one product's shell or the other,
 * so reaching it from the CRM would mean either leaving the CRM or minting a
 * second URL for one thing -- and two routes rendering the same panels is how
 * they drift.
 *
 * The cost is that it cannot be linked to, which is a fair trade: nobody sends
 * a colleague a link to their own avatar colour.
 *
 * Nothing here has a Save button. Every panel writes as you change it -- the
 * behaviour the page has always had -- so Close is genuinely just close.
 */
export function PersonalSettingsDialog({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  // Escape closes it, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-3 sm:p-4 sm:pt-12">
      {/*
        The scrim is a button so dismissing works by tap and by keyboard, and so
        it carries a label instead of being an unexplained dark area to a screen
        reader.
      */}
      <button
        type="button"
        aria-label="Close your settings"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />

      {/* `dvh` rather than `vh`, for the reason given in `Overlay`: on iOS the
          latter measures the viewport with the toolbars hidden, and the bottom
          of the card then sits under them. The phone's margins are `Overlay`'s
          too -- a 3rem drop from the top was a desktop's idea of breathing
          room, and on a handset it was the first setting's worth of screen. */}
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full sm:max-h-[calc(100dvh-4rem)] max-w-2xl flex-col overflow-hidden rounded-lg border border-line bg-app shadow-xl">
        <header className="flex items-center gap-2 border-b border-line bg-surface-2 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-content">Your settings</h2>
            <p className="text-[11px] text-muted">
              Yours across the Helpdesk and the CRM. Changes save as soon as you click
              away.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            onClick={onClose}
          >
            Close
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
          <PersonalSettings user={user} />
        </div>
      </div>
    </div>
  );
}
